import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  List,
  ChevronLeft,
  ChevronRight,
  Users,
  MapPin,
  Video,
} from "lucide-react";
import { callSheetApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

/* ─────────────────────────── colour helpers ─────────────────────────── */

// Project cover_color → chip styling. Kept opinionated so a busy month
// still reads as distinct blocks. Falls back to amber if unknown.
const CHIP_COLORS = {
  amber:  "bg-amber-500/25  text-amber-100  border-amber-500/50  hover:bg-amber-500/40",
  rose:   "bg-rose-500/25   text-rose-100   border-rose-500/50   hover:bg-rose-500/40",
  teal:   "bg-teal-500/25   text-teal-100   border-teal-500/50   hover:bg-teal-500/40",
  violet: "bg-violet-500/25 text-violet-100 border-violet-500/50 hover:bg-violet-500/40",
  blue:   "bg-blue-500/25   text-blue-100   border-blue-500/50   hover:bg-blue-500/40",
};

const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/* ─────────────────────── date utils (ISO strings) ────────────────────── */

const toISO = (d) => {
  // Local-date ISO (YYYY-MM-DD) — using .toISOString() would UTC-shift.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const todayISO = () => toISO(new Date());

/* ─────────────────────────────── page ───────────────────────────────── */

export default function CalendarPage() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");
  const [range, setRange] = useState("upcoming");
  const [showArchived, setShowArchived] = useState(false);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  useEffect(() => {
    (async () => {
      try {
        const list = await callSheetApi.listAll();
        setDays(list);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ─── Filtering pipeline (shared by both views) ─── */
  const filtered = useMemo(() => {
    const today = todayISO();
    return days.filter((d) => {
      if (!d.shoot_date) return false; // no scheduled date → not on calendar
      const status = d.project_status || "active";
      if (!showArchived && (status === "archived" || status === "cancelled")) {
        return false;
      }
      if (range === "upcoming" && d.shoot_date < today) return false;
      if (range === "past" && d.shoot_date >= today) return false;
      return true;
    });
  }, [days, range, showArchived]);

  const shootCount = filtered.length;

  return (
    <div className="min-h-screen max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      {/* ─── Header ─── */}
      <div className="flex items-center gap-3 mb-2">
        <CalendarIcon className="w-5 h-5 text-amber-500" />
        <div className="text-overline text-zinc-500">Production Schedule</div>
      </div>
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light text-zinc-100">
            Calendar
          </h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="calendar-count">
            {loading
              ? "Loading shoots…"
              : `${shootCount} shoot ${shootCount === 1 ? "day" : "days"} · ${range}`}
          </p>
        </div>

        {/* View toggle */}
        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(v) => v && setView(v)}
          className="border border-zinc-800 rounded-sm"
          data-testid="calendar-view-toggle"
        >
          <ToggleGroupItem
            value="calendar"
            data-testid="calendar-view-btn"
            className="px-4 py-2 data-[state=on]:bg-amber-500 data-[state=on]:text-black text-zinc-400"
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar
          </ToggleGroupItem>
          <ToggleGroupItem
            value="list"
            data-testid="list-view-btn"
            className="px-4 py-2 data-[state=on]:bg-amber-500 data-[state=on]:text-black text-zinc-400"
          >
            <List className="w-4 h-4 mr-2" />
            List
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* ─── Filters bar ─── */}
      <div className="flex items-center gap-6 flex-wrap mb-6 pb-6 border-b border-zinc-900">
        <ToggleGroup
          type="single"
          value={range}
          onValueChange={(v) => v && setRange(v)}
          className="border border-zinc-800 rounded-sm"
          data-testid="calendar-range-toggle"
        >
          {["upcoming", "past", "all"].map((r) => (
            <ToggleGroupItem
              key={r}
              value={r}
              data-testid={`range-${r}-btn`}
              className="px-3 py-1.5 text-xs uppercase tracking-wider font-mono data-[state=on]:bg-zinc-100 data-[state=on]:text-zinc-950 text-zinc-500"
            >
              {r}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className="flex items-center gap-2">
          <Switch
            data-testid="show-archived-switch"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label className="text-xs text-zinc-400 cursor-pointer" onClick={() => setShowArchived(!showArchived)}>
            Show archived &amp; cancelled
          </Label>
        </div>
      </div>

      {/* ─── Body ─── */}
      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="calendar-empty-state"
          className="border border-dashed border-zinc-800 rounded-md p-16 text-center text-zinc-500"
        >
          No shoots match the current filters.
        </div>
      ) : view === "calendar" ? (
        <MonthGrid
          cursor={cursor}
          setCursor={setCursor}
          days={filtered}
        />
      ) : (
        <ListView days={filtered} />
      )}
    </div>
  );
}

/* ─────────────────────────── Month Grid ─────────────────────────────── */

function MonthGrid({ cursor, setCursor, days }) {
  const { year, month } = cursor;
  const firstOfMonth = new Date(year, month, 1);
  const startDow = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = todayISO();

  // Bucket days by ISO date for fast lookup
  const byDate = useMemo(() => {
    const b = {};
    days.forEach((d) => {
      (b[d.shoot_date] = b[d.shoot_date] || []).push(d);
    });
    return b;
  }, [days]);

  // Build 6-week grid (42 cells)
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const dayNum = i - startDow + 1;
    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push(null);
    } else {
      const iso = toISO(new Date(year, month, dayNum));
      cells.push({ dayNum, iso, shoots: byDate[iso] || [] });
    }
  }
  // Trim trailing empty week
  while (cells.length > 35 && cells.slice(-7).every((c) => c === null)) {
    cells.length -= 7;
  }

  const step = (delta) => {
    const d = new Date(year, month + delta, 1);
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };
  const jumpToday = () => {
    const d = new Date();
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };

  return (
    <div data-testid="calendar-month-grid">
      {/* Month bar */}
      <div className="flex items-center justify-between mb-3">
        <h2
          data-testid="calendar-month-title"
          className="font-heading text-2xl text-zinc-100"
        >
          {MONTH_NAMES[month]} {year}
        </h2>
        <div className="flex items-center gap-1">
          <Button
            data-testid="calendar-today-btn"
            variant="ghost"
            onClick={jumpToday}
            className="text-xs uppercase tracking-wider font-mono text-zinc-400 hover:text-amber-500 hover:bg-zinc-900"
          >
            Today
          </Button>
          <Button
            data-testid="calendar-prev-month-btn"
            variant="ghost"
            size="icon"
            onClick={() => step(-1)}
            className="text-zinc-400 hover:text-amber-500 hover:bg-zinc-900"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            data-testid="calendar-next-month-btn"
            variant="ghost"
            size="icon"
            onClick={() => step(1)}
            className="text-zinc-400 hover:text-amber-500 hover:bg-zinc-900"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 gap-px mb-px">
        {DOW_LABELS.map((d) => (
          <div
            key={d}
            className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 py-2 px-3"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-px bg-zinc-900 border border-zinc-900 rounded-sm overflow-hidden">
        {cells.map((c, idx) => {
          if (c === null) {
            return <div key={idx} className="bg-zinc-950/60 min-h-[110px]" />;
          }
          const isToday = c.iso === today;
          return (
            <DayCell key={idx} cell={c} isToday={isToday} />
          );
        })}
      </div>
    </div>
  );
}

function DayCell({ cell, isToday }) {
  const { dayNum, shoots } = cell;
  const shown = shoots.slice(0, 3);
  const extra = shoots.length - shown.length;

  return (
    <div
      data-testid={`calendar-cell-${cell.iso}`}
      className={`bg-zinc-950 min-h-[110px] p-2 flex flex-col gap-1 ${
        isToday ? "ring-1 ring-inset ring-amber-500/60" : ""
      }`}
    >
      <div
        className={`text-xs font-mono ${
          isToday ? "text-amber-400 font-bold" : "text-zinc-500"
        }`}
      >
        {dayNum}
      </div>
      <div className="flex flex-col gap-0.5">
        {shown.map((s) => (
          <ShootChip key={s.id} shoot={s} />
        ))}
        {extra > 0 && (
          <div
            data-testid={`calendar-cell-more-${cell.iso}`}
            className="text-[10px] font-mono text-zinc-500 px-1"
          >
            +{extra} more
          </div>
        )}
      </div>
    </div>
  );
}

function ShootChip({ shoot }) {
  const cls = CHIP_COLORS[shoot.cover_color] || CHIP_COLORS.amber;
  const label =
    shoot.total_days > 1
      ? `${shoot.project_name} · D${shoot.day_number}`
      : shoot.project_name;
  return (
    <Link
      to={`/projects/${shoot.project_id}/call-sheet?day=${shoot.id}`}
      data-testid={`calendar-chip-${shoot.id}`}
      title={`${shoot.project_name}${shoot.day_label ? ` · ${shoot.day_label}` : ""}${
        shoot.total_days > 1 ? ` · Day ${shoot.day_number} of ${shoot.total_days}` : ""
      }`}
      className={`block text-[11px] leading-tight px-1.5 py-0.5 rounded-sm border truncate transition-colors ${cls}`}
    >
      {label}
    </Link>
  );
}

/* ─────────────────────────── List View ──────────────────────────────── */

function ListView({ days }) {
  // Group by date; sort chronologically ascending.
  const grouped = useMemo(() => {
    const g = {};
    days.forEach((d) => {
      (g[d.shoot_date] = g[d.shoot_date] || []).push(d);
    });
    // Sort each date's shoots by project name
    Object.values(g).forEach((arr) =>
      arr.sort((a, b) => a.project_name.localeCompare(b.project_name))
    );
    return Object.keys(g)
      .sort((a, b) => a.localeCompare(b))
      .map((date) => ({ date, shoots: g[date] }));
  }, [days]);

  return (
    <div className="space-y-8" data-testid="calendar-list-view">
      {grouped.map(({ date, shoots }) => (
        <ListDaySection key={date} date={date} shoots={shoots} />
      ))}
    </div>
  );
}

function ListDaySection({ date, shoots }) {
  const d = new Date(date + "T00:00:00");
  const isToday = date === todayISO();
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const nice = d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div data-testid={`list-date-${date}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <div
          className={`font-heading text-2xl ${
            isToday ? "text-amber-400" : "text-zinc-100"
          }`}
        >
          {nice}
        </div>
        <div className="text-overline text-zinc-500">
          {weekday}
          {isToday && " · Today"}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {shoots.map((s) => (
          <ShootRow key={s.id} shoot={s} />
        ))}
      </div>
    </div>
  );
}

function ShootRow({ shoot }) {
  const chipCls = CHIP_COLORS[shoot.cover_color] || CHIP_COLORS.amber;
  return (
    <Link
      to={`/projects/${shoot.project_id}/call-sheet?day=${shoot.id}`}
      data-testid={`list-row-${shoot.id}`}
      className="flex items-center gap-4 border border-zinc-800 rounded-sm px-4 py-3 hover:border-zinc-700 hover:bg-zinc-900/40 transition-colors group"
    >
      <span
        className={`inline-block w-1.5 h-10 rounded-sm ${chipCls.split(" ")[0]}`}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate group-hover:text-amber-500 transition-colors">
          {shoot.project_name}
        </div>
        <div className="flex items-center gap-3 mt-1 text-[11px] font-mono text-zinc-500">
          {shoot.total_days > 1 && (
            <span>
              Day {shoot.day_number} of {shoot.total_days}
              {shoot.day_label && ` · ${shoot.day_label}`}
            </span>
          )}
          {shoot.general_call && <span>Call {shoot.general_call}</span>}
          {shoot.location_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-2.5 h-2.5" />
              {shoot.location_count}
            </span>
          )}
          {shoot.crew_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-2.5 h-2.5" />
              {shoot.crew_count}
            </span>
          )}
          {shoot.talent_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <Video className="w-2.5 h-2.5" />
              {shoot.talent_count}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
